import { CacheUtil } from "@base/util/Cache";
import { PathUtil } from "@base/util/PathUtil";
import { StringUtil } from "@base/util/StringUtil";
import { mapRecord, mapToArray, PartialRecord } from "@base/util/TypeUtil";
import { CountryFlags } from "@biz/CountryFlags";
import { CurrencyData } from "@biz/CurrencyData";
import { BeosCountryCode, beosCountryCodes, LanguageCodeNumber, LanguageType, LanguageTypeValues } from "@biz/Definitions";
import { Access } from "@biz/definitions/AccessDefinitions";
import { IArticle, IShoppingArticle, IShoppingArticleWithBeosArticle } from "@biz/definitions/ArticleDefinitions";
import { BeosInputRawAuslieferungsart, BeosProductionLocation } from "@biz/definitions/BeosServiceDefinitions";
import { DiscountGroups, IBeosUser, IDiscount } from "@biz/definitions/DatabaseDefinitions";
import { ICurrencyData } from "@biz/definitions/PriceDefinitions";
import { ProductionLocation } from "@biz/definitions/SunSailDefinitions";
import { def } from "@biz/definitions/SunSailDefinitions.util";
import { ExternalOrderHelper } from "@biz/ExternalOrder";
import { languageFallbacks } from "@biz/language/Languages";
import { AccessHelper, AppUserAccessInput } from "@biz/logic/AccessHelper";
import { AppUser, BeosUser, IAppUser } from "@biz/models/AppUser";
import { SceneItem, SceneItemHelper, SceneItemSunSailNew } from "@biz/models/SceneItem";
import { PD } from "@biz/PD";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { VariantDataTypes } from "@biz/userProjects/new/IVariant";

const projectImages: Record<VariantDataTypes, string> = {
  sail2d: require("@biz/_assets/projectImages/sail2d.webp"),
  sail3d: require("@biz/_assets/projectImages/sail3d.webp"),
  shop: require("@biz/_assets/projectImages/shop.webp")
}

export interface ITransport {
  type: "number" | "unknown"
  value: number;
  articleId: string;
}

export class CommonLogic {
  static getLocalizedName(record: PartialRecord<LanguageType, string>, languageType: LanguageType, options?: { exact?: boolean }): string {
    const v = record[languageType];
    if (v) return v;
    if (!options?.exact) {
      for (const fallback of languageFallbacks) {
        const fb = record[fallback];
        if (fb)
          return fb;
      }
    }
    return "";
  }

  static getLanguageCodeFromName(name: LanguageType): LanguageCodeNumber {
    switch (name.toLowerCase()) {
      case "de_at":
        return LanguageCodeNumber.de_AT;
      case "de_de":
        return LanguageCodeNumber.de_AT;
      case "en_gb":
        return LanguageCodeNumber.en_GB;
      case "it_it":
        return LanguageCodeNumber.it_IT;
      case "es_es":
        return LanguageCodeNumber.es_ES;
      case "fr_fr":
        return LanguageCodeNumber.fr_FR;
    }
    return LanguageCodeNumber.de_AT;
  }

  static getLanguageSelectionNameFromCode(code: LanguageCodeNumber): string {
    switch (code) {
      case LanguageCodeNumber.de_AT:
        return "Deutsch";
      case LanguageCodeNumber.en_GB:
        return "English";
      case LanguageCodeNumber.fr_FR:
        return "Français";
      case LanguageCodeNumber.it_IT:
        return "Italiano";
      case LanguageCodeNumber.es_ES:
        return "Español";
    }
    return "?";
  }

  static getLanguageIdFromCode(code: LanguageCodeNumber): LanguageType | "" {
    const c = code - 1;
    if (c < 0 || c > LanguageTypeValues.length - 1) return "";
    return LanguageTypeValues[c];
  }

  static getLanguageNameShortFromCode(code: LanguageCodeNumber): string {
    switch (code) {
      case LanguageCodeNumber.de_AT:
        return "DE";
      case LanguageCodeNumber.en_GB:
        return "EN";
      case LanguageCodeNumber.fr_FR:
        return "FR";
      case LanguageCodeNumber.it_IT:
        return "IT";
      case LanguageCodeNumber.es_ES:
        return "ES";
    }
    return "?";
  }

  static getLanguages(): LanguageCodeNumber[] {
    return [
      LanguageCodeNumber.de_AT,
      LanguageCodeNumber.en_GB,
      LanguageCodeNumber.fr_FR,
      LanguageCodeNumber.it_IT,
      LanguageCodeNumber.es_ES,
    ];
  }

  static get3dLink(id: string, token: string): string {
    if (token) return "soliday://open?id=" + id + "&t=" + token;
    return "soliday://open?id=" + id;
  }

  static getAppHost(): string {
    const host = "https://www.soliday.eu/app";
    return host;
  }

  static getOpenSceneLink(id: string, token: string, language?: LanguageType): string {
    const host = CommonLogic.getAppHost();
    if (token) return `${host}/?openScene&id=${id}&t=${token}${language ? "&l=" + language : ""}`;
    return host;
  }

  static getOpenVariantLink(id: string, token: string): string {
    const host = CommonLogic.getAppHost();
    if (token) return `${host}/?openVariant&id=${id}&t=${token}`;
    return host;
  }

  static isSunSail(scene: SceneItem): scene is SceneItemSunSailNew {
    return scene.type === "sceneNew";
  }

  static hasOrderInfo(scene: SceneItem, userAccess: AppUserAccessInput): boolean {
    if (!AccessHelper.hasAccess(userAccess, Access.Soliday)) return false;
    switch (scene.type) {
      case "app": {
        const version = parseFloat(scene.appVersion);
        return version >= 1.35 && !!scene.files && scene.files.includes("additionalInfo.dat");
      }
      case "sceneNew":
        return !!scene.project.orderInfo;
    }
    return false;
  }

  public getObjLink(scene: SceneItem, token: string, user: IAppUser): string {
    if (!AccessHelper.hasAccess(user, Access.Soliday)) return "";
    switch (scene.type) {
      case "app": {
        const version = parseFloat(scene.appVersion);
        if (version >= 1.61 && scene.files && scene.files.includes("additionalInfo.dat")) return "../api/getObjLink?id=" + scene._id + "&token=" + token;
        return "";
      }
    }
    return "";
  }

  static getFormFinderLink(scene: SceneItem, token: string, user: AppUserAccessInput, index?: number): string {
    if (!AccessHelper.hasAccess(user, Access.Soliday)) return "";
    const url = "../api/getFormFinder?id=" + scene._id + "&token=" + token + (index !== undefined ? "&index=" + index : "");
    switch (scene.type) {
      case "app": {
        const version = parseFloat(scene.appVersion);
        if (version >= 2.48 && scene.files && scene.files.includes("additionalInfo.dat")) return url;
        return "";
      }
      case "sceneNew": {
        const data = scene.variant.data;
        return (data.type === "sail2d" || data.type === "sail3d") && data.sunSails ? url : "";
      }
    }
    return "";
  }

  static hasPackageDownload(scene: SceneItem, user: IAppUser): boolean {
    if (!AccessHelper.hasAccess(user, Access.Soliday)) return false;

    let count = 0;
    count = CommonLogic.hasOrderInfo(scene, user.access) ? count + 1 : count;
    count = CommonLogic.getFormFinderLink(scene, "", user) ? count + 1 : count;
    count = CommonLogic.getDealerQuoteLink(scene, user) ? count + 1 : count;

    if (count === 0) return false;
    return true;
  }

  static getPreviewBaseUrl(fileId: string, fileToken: string, sceneId: string): string {
    return `/api/projects/getPreview/S${sceneId}/T${fileToken}/I${fileId}`;
  }

  static getMissingProjectImageForType(type: VariantDataTypes | undefined): string {
    return projectImages[type || "sail3d"] || projectImages.sail3d;
  }

  static getDealerQuoteLink(scene: SceneItem, user: IAppUser): string {
    if (!AccessHelper.hasAccess(user, Access.Dealer)) return "";
    if (AppUser.getAccess(user) === Access.Dealer && scene.buyerCompanyId !== "" + AppUser.getCompanyId(user)) return "";
    if (scene.type === "app") {
      const version = parseFloat(scene.appVersion);
      if (version >= 1.35 && scene.files && scene.files.includes("quoteDealer.pdf")) {
        return PathUtil.join(scene.targetPath, "quoteDealer.pdf");
      }
    }
    if (scene.type === "sceneNew") {
      const offer = SceneItemHelper.getOffer(scene, "dealer");
      if (offer)
        return CommonLogic.getPreviewBaseUrl(offer.id, offer.token, scene._id);
    }
    return "";
  }

  static getCustomerQuoteLink(scene: SceneItem): string {
    if (scene.type === "app") {
      const version = parseFloat(scene.appVersion);
      if (version >= 1.35 && scene.files && scene.files.includes("quote.pdf") && scene.type === "app") {
        return PathUtil.join(scene.targetPath, "quote.pdf");
      }
    }
    return "";
  }

  public findBeosUserById(_id: number, _companyId: number, users: IBeosUser[]): IBeosUser | null {
    for (const user of users) {
      if (user.id === _id && BeosUser.getCompanyId(user) === _companyId) return user;
    }
    return null;
  }

  public findBeosUserByEmail(email: string, users: IBeosUser[]): IBeosUser | null {
    for (const user of users) {
      if (user._id === email) return user;
    }
    return null;
  }

  static enhanceArticle(sl: IShoppingArticle, articleMap: Record<string, IArticle>): IShoppingArticleWithBeosArticle {
    const slb = sl as IShoppingArticleWithBeosArticle;
    slb.beos = articleMap[sl.articleNumber];
    if (slb.included) {
      slb.included = mapRecord(slb.included, (item) => CommonLogic.enhanceArticle(item, articleMap))
    }
    return slb;
  }

  static enhanceArticles(shoppingList: IShoppingArticle[], articleMap: Record<string, IArticle>): IShoppingArticleWithBeosArticle[] {
    return shoppingList.map((sl) => {
      return CommonLogic.enhanceArticle({ ...sl }, articleMap)
    });
  }

  static flattenArticles(shoppingList: IShoppingArticle[]): IShoppingArticle[] {
    const result: IShoppingArticle[] = [];
    CommonLogic._flattenArticles(shoppingList, result);
    return result;
  }

  private static _flattenArticles(shoppingList: IShoppingArticle[], result: IShoppingArticle[]): void {
    shoppingList.forEach((sl) => {
      if (sl.partList?.list !== "none") {
        result.push(sl);
        if (sl.included) {
          const array = mapToArray(sl.included).filter((item) => item.partList?.list !== "none");
          if (array.length > 0)
            CommonLogic._flattenArticles(array, result);
        }
      }
    })
  }

  static getValidFrom(date: Date | undefined): number {
    return date ? new Date(date).getTime() : 0;
  }

  static getValidTo(date: Date | undefined): number {
    return date ? new Date(date).getTime() : 8640000000000000;
  }

  static isValidDiscount(dg: IDiscount, date: Date): boolean {
    if (!dg.validFrom || !dg.validTo)
      return true;
    const vf = CommonLogic.getValidFrom(dg.validFrom);
    const vt = CommonLogic.getValidTo(dg.validTo);
    const now = date.getTime();
    return now >= vf && now < vt;
  }

  static calcDiscountGroups(discounts: IDiscount[] | undefined, date: number, discountOverride: DiscountGroups | undefined): DiscountGroups {
    if (!discounts)
      return {};
    const dateObject = new Date(date);

    const ret: DiscountGroups = {};
    for (const d of discounts) {
      if (this.isValidDiscount(d, dateObject)) {
        const vt = CommonLogic.getValidTo(d.validTo);
        const vt2 = CommonLogic.getValidTo(ret[d.group]?.validTo);
        if (!ret[d.group] || vt < vt2)
          ret[d.group] = d;
      }
    }
    return { ...ret, ...discountOverride };
  }

  static getUniqueDiscountGroup(discounts: IDiscount[], group: string, date?: Date): IDiscount | undefined {
    if (date === undefined) date = new Date();
    let ret: IDiscount | undefined;
    for (const d of discounts) {
      if (d.group === group && this.isValidDiscount(d, date)) {
        if (!ret) ret = d;
        else {
          if (CommonLogic.getValidTo(ret.validTo) > CommonLogic.getValidTo(d.validTo)) ret = d;
        }
      }
    }
    return ret;
  }

  static findCurrency(currencies: ICurrencyData[], currencyId: string): ICurrencyData | undefined {
    for (const c of currencies) {
      if (c.id === currencyId) {
        return c;
      }
    }
    return undefined;
  }

  static #getCurrency(currencies: ICurrencyData[], currencyId: string | undefined, currencyFactorOverride?: number): ICurrencyData {
    const defaultCurrency = CurrencyData.EURO;
    if (currencyId) {
      const foundCurrency = CommonLogic.findCurrency(currencies, currencyId);
      if (foundCurrency) {
        if (currencyFactorOverride !== undefined) return { ...foundCurrency, factor: currencyFactorOverride };
        return foundCurrency;
      }
    }
    return defaultCurrency;
  }

  static getCurrency = CacheUtil.memo(CommonLogic.#getCurrency, "CommonLogic.getCurrency", {

  })

  static toBeosDate(timestamp: number): string {
    const d = new Date(timestamp);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    let year = "" + d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    year = year.substr(2);
    return [year, month, day].join("");
  }

  static toBeosCountry(location: ProductionLocation): string {
    switch (location) {
      case "AT":
        return "Österreich";
      case "BA":
        return "Bosnien";
    }
    return location;
  }

  static getFlagUrl(country: string): string {
    if (!country)
      return "";
    const url = def((CountryFlags as any)[country.toLowerCase()] || ""); // def for server...
    return url;
  }

  static getFlagUrlForCountryCode(countryCode: BeosCountryCode): string {
    if (!countryCode)
      return "";
    countryCode = countryCode.toUpperCase() as BeosCountryCode;
    const flag = beosCountryCodes[countryCode]?.flag
    if (flag) {
      return this.getFlagUrl(flag);
    }
    return this.getFlagUrl(countryCode);
  }

  static getAuslieferungsartUrl(auslieferungsart: BeosInputRawAuslieferungsart | undefined): string {
    if (!auslieferungsart)
      return "";
    const aa = auslieferungsart.toLowerCase();
    if (aa.includes("general logistics systems") || aa.includes("gls"))
      return require("@biz/_assets/deliveryIcons/gls.svg");
    if (aa.includes("paketdienst"))
      return require("@biz/_assets/deliveryIcons/car-side-light.svg");
    if (aa.includes("lkw"))
      return require("@biz/_assets/deliveryIcons/truck-light.svg");
    if (aa.includes("selbstabholer"))
      return require("@biz/_assets/deliveryIcons/dolly-light.svg");
    return "";
  }

  static fromBeosProductionLocation(location: BeosProductionLocation): ProductionLocation {
    switch (location) {
      case "AT":
        return "AT";
      case "BO":
        return "BA";
    }
    return location;
  }

  static calcPackageFolderName(
    projectName: string | undefined,
    companyName: string | undefined,
    pds: IProductDescriptor[] | undefined,
    externalOrderId: string | undefined,
    uploadTime: number | undefined,
  ): string {
    const nameArray: string[] = [];
    if (pds) {
      for (const pd of pds) {
        const sstName = PD.getNameShort(pd)
        if (!nameArray.includes(sstName)) nameArray.push(sstName);
      }
    }
    if (companyName) nameArray.push(companyName);
    if (projectName) nameArray.push(projectName);
    if (externalOrderId) nameArray.push(ExternalOrderHelper.toLongId(externalOrderId));
    if (uploadTime) nameArray.push(CommonLogic.toBeosDate(uploadTime));
    const folderName = StringUtil.replaceAll(nameArray.join("_"), " ", "_");
    return PathUtil.makeValidFileName(folderName);
  }

  static calcPackageFileName(
    projectName: string | undefined,
    companyName: string | undefined,
    pd: IProductDescriptor | undefined,
    as400Id: string | undefined,
    uploadTime: number | undefined
  ): string {
    const nameArray: string[] = [];
    if (pd) nameArray.push(PD.getNameShort(pd));
    if (companyName) nameArray.push(companyName);
    if (projectName) nameArray.push(projectName);
    if (as400Id) nameArray.push(ExternalOrderHelper.toLongId(as400Id));
    if (uploadTime) nameArray.push(CommonLogic.toBeosDate(uploadTime));
    const folderName = StringUtil.replaceAll(nameArray.join("_"), " ", "_");
    return PathUtil.makeValidFileName(folderName);
  }
}
